import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/config';
import { DINOS, DINO_ORDER } from '../data/dinos';
import { Music } from '../objects/Music';
import {
  fetchTopScores,
  formatTime,
  isLeaderboardConfigured,
  LeaderboardEntry,
} from '../services/leaderboard';

/**
 * Standalone leaderboard browser — opened from a button on Character
 * Select. Fetches top 10 from Back4App and shows rank/name/score/time.
 */
export class LeaderboardScene extends Phaser.Scene {
  constructor() {
    super('Leaderboard');
  }

  create() {
    Music.start('menu');
    this.cameras.main.setBackgroundColor(0x05070f);

    // Multi-color starfield using planet stripes.
    for (let i = 0; i < 80; i++) {
      const planet = DINO_ORDER[Phaser.Math.Between(0, 3)];
      this.add
        .image(Phaser.Math.Between(0, GAME_WIDTH), Phaser.Math.Between(0, GAME_HEIGHT), 'px')
        .setAlpha(Phaser.Math.FloatBetween(0.25, 1))
        .setTint(Math.random() < 0.18 ? DINOS[planet].stripe : 0xffffff);
    }

    this.add
      .text(GAME_WIDTH / 2, 22, 'TOP 10 GAUNTLET RUNS', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '12px',
        color: '#ffe23a',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5);

    const status = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'loading scores...', {
        fontFamily: '"VT323", monospace',
        fontSize: '12px',
        color: '#ffffff',
      })
      .setOrigin(0.5);

    if (!isLeaderboardConfigured()) {
      status.setText('leaderboard is not configured');
      status.setColor('#ff5a3a');
    } else {
      void this.loadAndRender(status);
    }

    // Back button.
    const backBtn = this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT - 22, 120, 22, 0x444466)
      .setStrokeStyle(2, 0xffffff)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 22, 'BACK', {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const goBack = () => this.scene.start('CharacterSelect');
    backBtn.on('pointerdown', goBack);
    this.input.keyboard?.once('keydown-ESC', goBack);
    this.input.keyboard?.once('keydown-BACKSPACE', goBack);
  }

  private async loadAndRender(status: Phaser.GameObjects.Text) {
    try {
      const scores = await fetchTopScores(10);
      status.destroy();
      this.renderRows(scores);
    } catch (e) {
      status.setText(`couldn't load (${(e as Error).message})`);
      status.setColor('#ff5a3a');
    }
  }

  private renderRows(scores: LeaderboardEntry[]) {
    const startY = 50;
    const rowH = 16;

    const headerStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: '"VT323", monospace',
      fontSize: '10px',
      color: '#9ad8ff',
    };
    this.add.text(28, startY, '#', headerStyle);
    this.add.text(56, startY, 'NAME', headerStyle);
    this.add.text(280, startY, 'SCORE', headerStyle).setOrigin(1, 0);
    this.add.text(GAME_WIDTH - 30, startY, 'TIME', headerStyle).setOrigin(1, 0);

    if (!scores.length) {
      this.add
        .text(GAME_WIDTH / 2, startY + 40, 'no scores yet — be the first!', {
          fontFamily: '"VT323", monospace',
          fontSize: '11px',
          color: '#ffffff',
        })
        .setOrigin(0.5);
      return;
    }

    scores.forEach((entry, i) => {
      const y = startY + 16 + i * rowH;
      const style: Phaser.Types.GameObjects.Text.TextStyle = {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#ffffff',
      };
      // Top 3 medals.
      if (i < 3) {
        const medalColors = [0xffe23a, 0xc8c8c8, 0xb87333];
        this.add
          .circle(20, y + 6, 5, medalColors[i])
          .setStrokeStyle(1, 0xffffff, 0.5);
      } else {
        this.add.text(28, y, String(i + 1).padStart(2, ' '), style).setOrigin(0, 0);
      }
      this.add.text(56, y, entry.name, style);
      this.add
        .text(280, y, String(entry.score).padStart(4, '0'), {
          ...style,
          color: '#ffe23a',
        })
        .setOrigin(1, 0);
      this.add
        .text(GAME_WIDTH - 30, y, formatTime(entry.timeMs), {
          ...style,
          color: '#9ad8ff',
        })
        .setOrigin(1, 0);
    });
  }
}
